import { isNotNil } from '~/utilities/type-guards';
import * as Sentry from '@sentry/react';
import { subdomain, environment } from '~/constantDefinitions';
import type { WistiaError } from '~/datadogRum/WistiaError';
import { isWistiaError } from '~/datadogRum/WistiaError';
import type { Product, Pillar } from '../../types/wistia';

const environmentsToLog = new Set<string>(['production', 'staging', 'stagingBranchDeploy']);

interface ErrorReportingContext {
  [key: string]: unknown; // from Levi: we should consider making these primitive values
  pillar: Pillar;
  product: Product;
  attachment?: Attachment;
  json?: unknown;
}

interface Attachment {
  data: Uint8Array | string;
  filename: string;
  contentType?: string;
}

/**
 * Reports an error to Sentry.
 *
 * In dev and test environments where Sentry is not available, the error is logged to the console.
 *
 * @param error - The error to report
 * @param context - An optional object containing extra context about the error
 */
export const reportError = (error: Error | WistiaError, context?: ErrorReportingContext): void => {
  if (environmentsToLog.has(environment)) {
    Sentry.captureException(error, (scope) => {
      if (isNotNil(context)) {
        const { attachment, json, ...restContext } = context;
        if (isNotNil(attachment)) {
          scope.addAttachment(attachment);
        }
        if (isNotNil(json)) {
          try {
            scope.setExtra('json', JSON.stringify(json));
            // eslint-disable-next-line no-empty
          } catch {}
        }
        scope.setTags({
          ...restContext,
          pillar: context.pillar,
          product: context.product,
          // eslint-disable-next-line camelcase
          wistia_account_key: subdomain,
        });
      } else if (isWistiaError(error)) {
        scope.setTags({
          pillar: error.pillar,
          product: error.product,
          // eslint-disable-next-line camelcase
          wistia_account_key: subdomain,
        });
      } else {
        // Do nothing
      }
      return scope;
    });
  }

  if (isNotNil(window.DD_RUM) && isNotNil(window.DD_RUM.addError)) {
    if (isNotNil(context)) {
      window.DD_RUM.addError(error, { ...context });
    } else {
      window.DD_RUM.addError(error);
    }
  } else {
    // for dev/test environments that do not have Datadog RUM installed
    console.error(error, { ...context }); // eslint-disable-line no-console
  }
};
